import { apiClient } from '@/core/apiClient';
import type { User, UsersListParams } from './types';

export const usersApi = {
  search: async (query: Record<string, any>) => {
    const response = await apiClient.get('/app-users/search', { params: query });
    return response.data; // { data, total, page, pageSize }
  },
  list: async (params: UsersListParams = {}) => {
    // Backwards compatibility; prefer search()
    const query: any = {};
    if (params.q) query.q = params.q;
    if (params.pageSize) query.pageSize = params.pageSize;
    if (params.page) query.page = params.page;
    return usersApi.search(query);
  },

  count: async (where?: Record<string, any>) => {
    const response = await apiClient.get('/users/count', { params: where ? { where } : undefined });
    return response.data;
  },

  getById: async (id: number): Promise<User> => {
    const params: Record<string, any> = {
      'filter[fields][id]': true,
      'filter[fields][fullName]': true,
      'filter[fields][email]': true,
      'filter[fields][roleId]': true,
      'filter[fields][isActive]': true,
      'filter[fields][phone]': true,
      'filter[fields][birthDate]': true,
      'filter[fields][createdAt]': true,
      'filter[fields][updatedAt]': true,
      'filter[include][0][relation]': 'role',
    };
    const response = await apiClient.get(`/app-users/${id}`, { params });
    const body = response.data;
    return (body?.data ?? body) as User;
  },

  create: async (user: any): Promise<User> => {
    const response = await apiClient.post('/app-users', user);
    const body = response.data;
    return body?.data ?? body;
  },

  update: async (id: number, user: any): Promise<User> => {
    const response = await apiClient.patch(`/app-users/${id}`, user);
    const body = response.data;
    return body?.data ?? body;
  },

  replace: async (id: number, user: any): Promise<User> => {
    const response = await apiClient.put(`/app-users/${id}`, user);
    const body = response.data;
    return body?.data ?? body;
  },

  delete: async (id: number) => {
    await apiClient.delete(`/app-users/${id}`);
  },

  listRoles: async () => {
    const response = await apiClient.get('/roles', { params: { 'filter[order]': 'name ASC' } });
    return response.data;
  },

  listSheltersSimple: async () => {
    const params: Record<string, any> = {
      'filter[fields][id]': true,
      'filter[fields][name]': true,
      'filter[order]': 'name ASC',
    };
    const response = await apiClient.get('/shelters', { params });
    return response.data;
  },

  listUserShelters: async (userId: number) => {
    const response = await apiClient.get(`/app-users/${userId}/shelters`);
    return response.data; // expect array
  },

  putUserShelters: async (userId: number, items: Array<{ shelterId: number; roleInShelter?: string }>) => {
    const response = await apiClient.put(`/app-users/${userId}/shelters`, items);
    return response.data;
  },

  // Shelter-centric user listing
  listShelterUsers: async (
    shelterId: number,
    params: { q?: string; page?: number; pageSize?: number; sort?: string; dir?: string }
  ) => {
    const response = await apiClient.get(`/shelters/${shelterId}/users`, { params });
    return response.data; // { data, total, page, pageSize }
  },

  // Candidates for linking (role=user and without any shelter)
  listShelterCandidates: async (
    shelterId: number,
    params: { q?: string; page?: number; pageSize?: number; sort?: string; dir?: string }
  ) => {
    const response = await apiClient.get(`/shelters/${shelterId}/candidates`, { params });
    return response.data; // { data, total, page, pageSize }
  },

  // Link a user to a shelter (POST)
  linkUserToShelter: async (
    userId: number,
    payload: { shelterId: number; roleInShelter?: string }
  ) => {
    const response = await apiClient.post(`/app-users/${userId}/shelters`, payload);
    return response.data;
  },

  // Update only the roleInShelter for a given link
  updateUserShelterRole: async (
    userId: number,
    shelterId: number,
    roleInShelter?: string
  ) => {
    const response = await apiClient.patch(`/app-users/${userId}/shelters/${shelterId}`, {
      roleInShelter,
    });
    return response.data;
  },

  // Unlink user from shelter
  unlinkUserFromShelter: async (userId: number, shelterId: number) => {
    const response = await apiClient.delete(`/app-users/${userId}/shelters/${shelterId}`);
    return response.data;
  },
};
